<template>
    <div id="modal-advanced-settings">

        <div class="rp-overlay"></div>

        <div class="rp-modal" style="width: 600px;">

            <div class="rp-modal-header">
                <h3 class="rp-modal-title">{{ $t('Advanced Settings') }}</h3>
                <button class="rp-btn-dismiss" @click="$emit('closeModal')">
                    <XIcon class="w-5 h-5" />
                </button>
            </div>

            <div class="bg-white p-8 overflow-y-scroll" style="max-height: 70vh;">
                
                <!-- Image Size -->
                <div class="mb-5 text-left">
                    <h3 class="text-base font-medium mb-2">{{ $t('Image Size') }}</h3>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="block text-gray-700 mb-2">{{ $t('Width (px)') }}</label>
                            <input
                                type="number"
                                v-model.number="localSettings.width"
                                :max="props.dataset.type === 'ANOMALY' ? 512 : undefined"
                                placeholder="Width"
                                class="form-control py-2 px-3 border rounded w-100"
                                required />
                        </div>
                        <div>
                            <label class="block text-gray-700 mb-2">{{ $t('Height (px)') }}</label>
                            <input
                                type="number"
                                v-model.number="localSettings.height"
                                :max="props.dataset.type === 'ANOMALY' ? 512 : undefined"
                                placeholder="Height"
                                class="form-control py-2 px-3 border rounded w-100"
                                required />
                        </div>
                    </div>
                </div>

                <!-- Task -->
                <div class="mb-5 text-left">
                    <h3 class="text-base font-medium mb-2">{{ $t('Task') }}</h3>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="block text-gray-700 mb-2">{{ $t('Type') }}</label>
                            <select v-model="localSettings.taskType" class="form-select py-2 px-3 border rounded w-full" required>
                                <option value="" disabled>{{ $t('Select task type') }}</option>
                                <option v-for="option in taskTypeOptions" :key="option.value" :value="option.value">{{ $t(option.label) }}</option>
                            </select>
                        </div>
                        <div>
                            <label class="block text-gray-700 mb-2">{{ $t('Model') }}</label>
                            <select v-model="localSettings.taskModel" class="form-select py-2 px-3 border rounded w-full" required>
                                <option value="" disabled>{{ $t('Select task model') }}</option>
                                <option v-for="option in taskModelOptions" :key="option.value" :value="option.value">{{ $t(option.label) }}</option>
                            </select>
                        </div>
                    </div>
                </div>

                <!-- Task Config -->
                <div v-if="localSettings.taskModel" class="mb-5 text-left">
                    <h3 class="text-base font-medium mb-2">{{ $t('Task Config') }}</h3>
                    <div v-for="option of taskParamsOptions" :key="option.id">
                        <div v-if="!option.hide" class="mt-2">
                            <label class="block text-gray-700 mb-2">{{ option.name }}</label>
                            
                            <template v-if="option.type === 'option'">
                                <select v-model="localSettings.taskConfig[option.id]" class="form-select py-2 px-3 border rounded w-full" required>
                                    <option v-for="v of option.values" :key="v">{{ v }}</option>
                                </select>
                            </template>

                            <template v-else-if="option.type === 'range'">
                                <div class="flex items-center mt-2">
                                    <input 
                                        type="number" 
                                        v-model.number="localSettings.taskConfig[option.id]" 
                                        :placeholder="option.name"
                                        class="form-control py-2 px-3 border rounded w-1/3" 
                                        :min="option.min"
                                        :max="option.max"
                                        :step="option.step"
                                        required />
                                    <div class="flex-grow ml-5">
                                        <Slider v-model="localSettings.taskConfig[option.id]" :min="option.min" :max="option.max" :step="option.step" :tooltips="false"/>
                                    </div>
                                </div>
                            </template>

                        </div>
                    </div>
                </div>

                <!-- Batch Size -->
                <div class="mb-5 text-left">
                    <label class="text-base font-medium">{{ $t('Batch Size') }}</label>
                    <div class="flex items-center mt-2">
                        <input 
                            type="number" 
                            v-model.number="localSettings.batchSize" 
                            placeholder="Batch Size" 
                            class="form-control py-2 px-3 border rounded w-1/3" 
                            min="1" 
                            :max="props.dataset.type === 'ANOMALY' ? 1 : 32"
                            required />
                        <div class="flex-grow ml-5">
                            <Slider v-model="localSettings.batchSize" :min="1" :max="props.dataset.type === 'ANOMALY' ? 2 : 32" :tooltips="false"/>
                        </div>
                    </div>
                </div>

                <!-- Patience -->
                <div class="mb-5 text-left">
                    <label class="text-base font-medium">{{ $t('Patience') }}</label>
                    <div class="flex items-center mt-2">
                        <input 
                            type="number" 
                            v-model.number="localSettings.patience" 
                            placeholder="Patience" 
                            class="form-control py-2 px-3 border rounded w-1/3" 
                            min="0" 
                            max="100"
                            required />
                        <div class="flex-grow ml-5">
                            <Slider v-model="localSettings.patience" :min="0" :max="100" :tooltips="false"/>
                        </div>
                    </div>
                </div>

            </div>

            <!-- Action buttons -->
            <div class="rp-modal-body-buttons gap-3 mb-4" style="justify-content: center;">
                <button class="rp-btn-secondary" @click="$emit('closeModal')">
                    {{ $t('Cancel') }}
                </button>
                <button class="rp-btn-success" @click="$emit('applySettings', localSettings)">
                    {{ $t('Apply') }}
                </button>
            </div>
            
        </div>

    </div>
</template>

<script setup>
import { onMounted, ref, watch, toRaw, computed } from "vue";
import Slider from "@vueform/slider";

const props = defineProps({
    dataset: {
        type: Object,
        required: true,
    },
    advancedSettings: {
        type: Object,
        required: true,
    }
});

const taskTypeOptions = computed(() => {
    switch (props.dataset.type) {
        case 'MULTILABEL':
            return [
                { value: 'segmentation', label: 'Segmentation' },
                { value: 'imageObjectDetection', label: 'Detection' }
            ];
        case 'MULTICLASS':
            return [
                { value: 'classification', label: 'Classification' }
            ];
        case 'imageObjectDetection':
            return [
                { value: 'imageObjectDetection', label: 'Detection' }
            ];
        case 'ANOMALY':
            return [
                { value: 'anomalyDetection', label: 'Anomaly' }
            ];
        default:
            return [];
    }
});

const taskModelOptions = computed(() => {
    switch (props.dataset.type) {
        case 'MULTILABEL':
        case 'imageObjectDetection':
            return [
                { value: 'yolov8', label: 'V1' },
                { value: 'yolov11', label: 'V2' }
            ];
        case 'MULTICLASS':
            return [
                { value: 'yolov8', label: 'V1' },
                { value: 'yolov11', label: 'V2' }
            ];
        case 'ANOMALY':
            return [
                //{ value: 'patchcore', label: 'V1' },
                { value: 'padim', label: 'V2' },
                //{ value: 'cflow', label: 'V3' }
            ];
        default:
            return [];
    }
});

const taskParamsOptions = computed(() => {
    switch (localSettings.value.taskModel) {
        case 'yolov8':
        case 'yolov11':
            return [
                {
                    id: 'size',
                    name: 'Model Size', 
                    type: 'option',
                    default: 'Nano',
                    values: ['Nano','Small', 'Medium', 'Large', 'Extra Large'] 
                }
            ];
        case 'patchcore':
            return [
                ...sharedAnomalyOptions,
                {
                    id: 'coreset_sampling_ratio',
                    name: 'Coreset Sampling',
                    type: 'range',
                    default: 0.02,
                    min: 0.01,
                    max: 0.1,
                    step: 0.01
                },
                {
                    id: 'n_neighbors',
                    name: 'Number of Neighbors',
                    type: 'range',
                    default: 5,
                    min: 1,
                    max: 10,
                    step: 1
                }
            ];
        case 'padim':
            return [
                ...sharedAnomalyOptions,
                {
                    id: 'n_features',
                    name: 'Number of Features',
                    type: 'range',
                    default: 100,
                    min: 10,
                    max: 500,
                    step: 1
                }
            ];
        case 'cflow':
            return [
                ...sharedAnomalyOptions,
                {
                    id: 'fiber_batch_size',
                    name: 'Fiber Batch Size',
                    type: 'range',
                    default: 64,
                    min: 16,
                    max: 256,
                    step: 1
                },
                {
                    id: 'condition_vector',
                    name: 'Number of Condition Vectors',
                    type: 'range',
                    default: 128,
                    min: 32,
                    max: 512,
                    step: 1
                }
            ];
        default:
            return [];
    }
});

const sharedAnomalyOptions = [
    {
        id: 'pre_trained',
        default: true,
    },
    {
        id: 'backbone',
        name: 'Backbone',
        type: 'option',
        default: 'resnet18',
        values: ['resnet18']
        //values: ['resnet18', 'resnet50', 'wideresnet50']
    }
];

const localSettings = ref({
    width: 0,
    height: 0,
    time: 5,
    batchSize: 16,
    patience: 0,
    earlyStopping: false,
    taskType: "",
    taskModel: "",
    taskConfig: {}
});

onMounted(() => {

    
    if (props.advancedSettings) {
        localSettings.value = JSON.parse(JSON.stringify(props.advancedSettings));
        
    }
});

watch(() => localSettings.value.width, (newWidth) => {
  if (props.dataset?.type === 'ANOMALY' && newWidth > 512) {
    localSettings.value.width = 512;
  }
});

watch(() => localSettings.value.height, (newHeight) => {
  if (props.dataset?.type === 'ANOMALY' && newHeight > 512) {
    localSettings.value.height = 512;
  }
});

watch(() => props.advancedSettings, (newVal) => {
    if (newVal) {
        // Merge new values with existing localSettings, preserving structure
        Object.assign(localSettings.value, JSON.parse(JSON.stringify(toRaw(newVal))));
        
        // Ensure taskConfig exists as an object
        if (!localSettings.value.taskConfig) {
            localSettings.value.taskConfig = {};
        }
        
    }
}, { immediate: true });

watch(taskParamsOptions, (options) => {

    
    if (options.length === 0) {
        
        return;
    }

    // Only reset taskConfig if we're actually changing to a different model
    // Don't reset if we're just initializing for the first time
    if (localSettings.value.taskModel && 
        localSettings.value.taskModel !== props.advancedSettings.taskModel && 
        props.advancedSettings.taskModel) {
        localSettings.value.taskConfig = {}
    }
    
    options.forEach(option => {
        // Set default value only if the property doesn't exist or is null/undefined
        if (localSettings.value.taskConfig[option.id] == null) {
            
            localSettings.value.taskConfig[option.id] = option.default
        } 
    })
    
   }, { immediate: true })
</script>